import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaRedo, FaClock, FaCheckCircle } from "react-icons/fa";
import { showToast } from "../../utils/customToast";
import {
  generateSecureOTP,
  sendWhatsappOtp,
  formatTime,
} from "./authHelper";
import "./ForgotPassword.css";

function OtpVerification() {
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve state passed from ForgotPassword
  const mobile = location.state?.mobile || "";
  const initialOtp = location.state?.generatedOtp || "";
  const initialExpiresAt = location.state?.expiresAt || Date.now() + 5 * 60 * 1000;
  const user = location.state?.user || null;

  // Local state for expected OTP and timer
  const [expectedOtp, setExpectedOtp] = useState(initialOtp);
  const [expiresAt, setExpiresAt] = useState(initialExpiresAt);
  const [timeLeft, setTimeLeft] = useState(() => {
    const remaining = Math.max(0, Math.floor((initialExpiresAt - Date.now()) / 1000));
    return remaining > 0 ? remaining : 300;
  });

  // 4 individual digit boxes
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Redirect back if user lands here directly without a mobile number
  useEffect(() => {
    if (!mobile) {
      showToast.warn("Please enter your registered mobile number first.");
      navigate("/forgot-password", { replace: true });
    }
  }, [mobile, navigate]);

  // 5-Minute Countdown Timer
  useEffect(() => {
    const timerInterval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(timerInterval);
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [expiresAt]);

  const isExpired = timeLeft <= 0;

  // Handle single digit typing & auto-focus next
  const handleDigitChange = (index, value) => {
    // Only allow single numeric character
    const cleaned = value.replace(/[^0-9]/g, "");

    const newDigits = [...otpDigits];

    if (cleaned.length > 1) {
      // Handle paste in an input
      const pasteDigits = cleaned.slice(0, 4).split("");
      pasteDigits.forEach((d, i) => {
        if (i < 4) newDigits[i] = d;
      });
      setOtpDigits(newDigits);
      const nextIdx = Math.min(pasteDigits.length, 3);
      inputRefs[nextIdx]?.current?.focus();
    } else {
      newDigits[index] = cleaned;
      setOtpDigits(newDigits);

      if (cleaned && index < 3) {
        inputRefs[index + 1]?.current?.focus();
      }
    }

    if (hasError) {
      setHasError(false);
      setErrorMessage("");
    }
  };

  // Handle backspace to focus previous box
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs[index - 1]?.current?.focus();
    }
  };

  // Handle clipboard paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 4);
    if (!pasteData) return;

    const newDigits = ["", "", "", ""];
    for (let i = 0; i < pasteData.length; i++) {
      newDigits[i] = pasteData[i];
    }
    setOtpDigits(newDigits);

    const targetFocus = Math.min(pasteData.length, 3);
    inputRefs[targetFocus]?.current?.focus();

    if (hasError) {
      setHasError(false);
      setErrorMessage("");
    }
  };

  // Match OTP
  const handleVerifyOtp = (e) => {
    if (e) e.preventDefault();
    if (verifying) return;

    const enteredOtp = otpDigits.join("");

    if (enteredOtp.length !== 4) {
      setHasError(true);
      setErrorMessage("Please enter the complete 4-digit OTP.");
      showToast.warn("Please enter all 4 digits of the OTP.");
      return;
    }

    if (isExpired) {
      setHasError(true);
      setErrorMessage("This OTP has expired. Please click 'Resend OTP' to receive a new code.");
      showToast.error("OTP expired. Please resend.");
      return;
    }

    setVerifying(true);

    // Compare entered OTP with expected state value
    if (enteredOtp === String(expectedOtp).trim()) {
      showToast.success("OTP verified successfully!");
      navigate("/reset-password", {
        state: {
          mobile,
          user,
          verified: true,
        },
      });
    } else {
      setHasError(true);
      setErrorMessage("Invalid OTP. The code you entered does not match. Please try again.");
      showToast.error("Invalid OTP entered.");
      setVerifying(false);
    }
  };

  // Resend OTP via WhatsApp
  const handleResendOtp = async () => {
    if (resending) return;
    setResending(true);
    setHasError(false);
    setErrorMessage("");

    try {
      // 1. Generate new 4-digit secure OTP
      const newOtp = generateSecureOTP();
      const newExpiresAt = Date.now() + 5 * 60 * 1000;

      console.log(`[DEV MODE] Resent 4-Digit OTP for ${mobile}:`, newOtp);

      // 2. WhatsApp API dispatch
      const sendResult = await sendWhatsappOtp(mobile, newOtp);

      if (sendResult && (sendResult.success === true || sendResult.apiResponse)) {
        setExpectedOtp(newOtp);
        setExpiresAt(newExpiresAt);
        setTimeLeft(300);
        setOtpDigits(["", "", "", ""]);
        inputRefs[0]?.current?.focus();
        showToast.success("A new 4-digit OTP has been sent to your WhatsApp!");
      } else {
        const msg = sendResult?.message || "Failed to resend OTP. Please try again.";
        setErrorMessage(msg);
        showToast.error(msg);
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setErrorMessage("Could not resend OTP. Please check your connection.");
      showToast.error("Failed to resend OTP.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Brand Logo */}
        <div className="auth-logo">
          <img src="/media/response_info.jpg" alt="Response Infoway" />
        </div>

        {/* Step Progress Indicator */}
        <div className="auth-steps-indicator">
          <div className="auth-step-dot completed" title="Step 1: Mobile Number"></div>
          <div className="auth-step-dot active" title="Step 2: OTP Verification"></div>
          <div className="auth-step-dot" title="Step 3: Reset Password"></div>
        </div>

        {/* Card Header */}
        <div className="auth-header">
          <h2 className="auth-title">Verify OTP</h2>
          <p className="auth-subtitle">
            Enter the 4-digit code sent to{" "}
            <span className="highlight-number">+91 {mobile}</span> on WhatsApp.
          </p>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="auth-alert-error">
            <span>{errorMessage}</span>
          </div>
        )}

        {/* 4-Digit OTP Form */}
        <form className="auth-form" onSubmit={handleVerifyOtp}>
          <div className="otp-inputs-row" onPaste={handlePaste}>
            {otpDigits.map((digit, idx) => (
              <input
                key={idx}
                ref={inputRefs[idx]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                className={`otp-digit-box ${digit ? "filled" : ""} ${hasError ? "error" : ""}`}
                value={digit}
                onChange={(e) => handleDigitChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                autoFocus={idx === 0}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {/* Expiration Timer & Resend Button */}
          <div className="otp-meta-row">
            <span className={`otp-timer ${isExpired ? "expired" : ""}`}>
              <FaClock />
              {isExpired ? (
                <span>OTP Expired</span>
              ) : (
                <span>Expires in {formatTime(timeLeft)}</span>
              )}
            </span>

            <button
              type="button"
              className="otp-resend-btn"
              onClick={handleResendOtp}
              disabled={resending}
              title="Click to receive a new OTP on WhatsApp"
            >
              <FaRedo className={resending ? "fa-spin" : ""} />
              {resending ? "Resending..." : "Resend OTP"}
            </button>
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={verifying}
          >
            {verifying ? (
              <span className="btn-loading-wrapper">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Verifying...</span>
              </span>
            ) : (
              <>
                <FaCheckCircle />
                <span>Verify & Proceed</span>
              </>
            )}
          </button>
        </form>

        {/* Footer Change Mobile Number Link */}
        <div className="auth-footer">
          <Link to="/forgot-password" className="auth-back-link">
            <FaArrowLeft /> Change Mobile Number
          </Link>
        </div>
      </div>
    </div>
  );
}

export default OtpVerification;
